import React, { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Icon from "../../components/AppIcon"
import Button from "../../components/ui/Button"
import { authHelpers } from "../../firebaseClient"
import MentorshipChat from "../../components/mentorship/MentorshipChat"
import MentorshipAppointments from "../../components/mentorship/MentorshipAppointments"
import MentorAnalytics from "../../components/mentorship/MentorAnalytics"
import {
  subscribeAllMyMentees,
  logMentorshipSession,
  subscribeMentorshipSessions,
  addMentorshipNote,
  subscribeMentorshipNotes,
} from "../../utils/mentorshipHelpers"

const MentorWorkspace = () => {
  const navigate = useNavigate()
  const [currentUser, setCurrentUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [mentees, setMentees] = useState([])
  const [selected, setSelected] = useState(null) // the active mentorship
  const [subTab, setSubTab] = useState("overview") // "overview" | "messages" | "appointments"
  const [pageTab, setPageTab] = useState("mentees") // "mentees" | "analytics"

  const [sessions, setSessions] = useState([])
  const [notes, setNotes] = useState([])
  const [sessionNotes, setSessionNotes] = useState("")
  const [newNote, setNewNote] = useState("")
  const [logging, setLogging] = useState(false)
  const [savingNote, setSavingNote] = useState(false)

  useEffect(() => {
    const load = async () => {
      const { session } = await authHelpers.getSession()
      if (!session) {
        navigate("/authentication-portal", { replace: true })
        return
      }
      setCurrentUser(session.user)
      setLoading(false)
    }
    load()
  }, [navigate])

  useEffect(() => {
    if (!currentUser) return
    return subscribeAllMyMentees(currentUser.uid, setMentees)
  }, [currentUser])

  useEffect(() => {
    if (!selected) { setSessions([]); setNotes([]); return }
    const u1 = subscribeMentorshipSessions(selected.orgId, selected.id, setSessions)
    const u2 = subscribeMentorshipNotes(selected.orgId, selected.id, setNotes)
    return () => { u1(); u2(); }
  }, [selected])

  useEffect(() => { setSubTab("overview") }, [selected?.id])

  const handleLogSession = async () => {
    if (!selected || !sessionNotes?.trim()) return
    setLogging(true)
    await logMentorshipSession(selected.orgId, selected.id, { notes: sessionNotes.trim() })
    setLogging(false)
    setSessionNotes("")
  }

  const handleAddNote = async () => {
    if (!selected || !newNote?.trim()) return
    setSavingNote(true)
    await addMentorshipNote(selected.orgId, selected.id, newNote.trim())
    setSavingNote(false)
    setNewNote("")
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading your mentor workspace...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Mentor Workspace - Nesolearn</title></Helmet>
      <NavigationBar />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-8">
        <Link
          to="/user-dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4"
        >
          <Icon name="ArrowLeft" size={16} />
          Back to Dashboard
        </Link>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Mentees</h1>
          <p className="text-muted-foreground mt-1">People you're currently mentoring, across every organization.</p>
        </div>

        <div className="mb-6">
          <MentorAnalytics mentorUid={currentUser.uid} variant="summary" />
        </div>

        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setPageTab("mentees")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
              pageTab === "mentees" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            My Mentees
          </button>
          <button
            onClick={() => setPageTab("analytics")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
              pageTab === "analytics" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
            }`}
          >
            Analytics
          </button>
        </div>

        {pageTab === "analytics" ? (
          <MentorAnalytics mentorUid={currentUser.uid} variant="full" />
        ) : mentees?.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Icon name="HeartHandshake" size={28} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">You don't have any mentees yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 space-y-2">
              {mentees?.map((m) => (
                <button
                  key={m?.id}
                  onClick={() => setSelected(m)}
                  className={`w-full text-left bg-card border rounded-lg p-3 transition-smooth ${
                    selected?.id === m?.id ? "border-accent bg-accent/5" : "border-border hover:bg-muted/50"
                  }`}
                >
                  <p className="text-sm font-medium text-foreground">{m?.menteeName}</p>
                  {m?.goal && <p className="text-xs text-muted-foreground mt-0.5">{m?.goal}</p>}
                </button>
              ))}
            </div>

            <div className="lg:col-span-2 space-y-4">
              {!selected ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground text-sm">
                  Select a mentee to view sessions and notes.
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSubTab("overview")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "overview" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Overview
                    </button>
                    <button
                      onClick={() => setSubTab("messages")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "messages" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Messages
                    </button>
                    <button
                      onClick={() => setSubTab("appointments")}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                        subTab === "appointments" ? "bg-accent text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      Appointments
                    </button>
                  </div>

                  {subTab === "messages" && (
                    <MentorshipChat
                      orgId={selected.orgId}
                      mentorshipId={selected.id}
                      currentUser={{ uid: currentUser.uid, name: currentUser.displayName || currentUser.email?.split("@")[0] || "Mentor" }}
                      otherUid={selected.menteeUid}
                      otherName={selected.menteeName}
                    />
                  )}

                  {subTab === "appointments" && (
                    <MentorshipAppointments
                      orgId={selected.orgId}
                      mentorshipId={selected.id}
                      currentUser={{ uid: currentUser.uid, name: currentUser.displayName || currentUser.email?.split("@")[0] || "Mentor" }}
                      otherName={selected.menteeName}
                      canPropose={true}
                    />
                  )}

                  {subTab === "overview" && (
                <>
                  <div className="bg-card border border-border rounded-xl p-5 space-y-3">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Log a Session</h2>
                    <textarea
                      rows={3}
                      value={sessionNotes}
                      onChange={(e) => setSessionNotes(e?.target?.value)}
                      placeholder="What did you cover in this check-in?"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Button variant="default" size="sm" loading={logging} onClick={handleLogSession}>
                      Log Session
                    </Button>
                  </div>

                  <div className="bg-card border border-border rounded-xl p-5 space-y-2">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Session History</h2>
                    {sessions?.length === 0 && <p className="text-sm text-muted-foreground">No sessions logged yet.</p>}
                    {sessions?.map((s) => (
                      <div key={s?.id} className="py-2 border-b border-border last:border-0">
                        <p className="text-sm text-foreground">{s?.notes}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {s?.completedAtMs ? new Date(s.completedAtMs).toLocaleDateString() : ""}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-card border border-border rounded-xl p-5 space-y-3">
                    <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Private Notes</h2>
                    <p className="text-xs text-muted-foreground -mt-2">Only visible to you — your mentee can't see these.</p>
                    <textarea
                      rows={2}
                      value={newNote}
                      onChange={(e) => setNewNote(e?.target?.value)}
                      placeholder="Add a private note..."
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    <Button variant="outline" size="sm" loading={savingNote} onClick={handleAddNote}>
                      Add Note
                    </Button>
                    {notes?.map((n) => (
                      <div key={n?.id} className="py-2 border-b border-border last:border-0">
                        <p className="text-sm text-foreground">{n?.text}</p>
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

export default MentorWorkspace